import * as TooltipPrimitive from 'radix-ui/tooltip';
import type { ReactNode } from 'react';
import { MOTION } from '../motion';

/**
 * A tooltip is a mouse affordance. It NEVER carries information a touch user
 * needs - that was one of Twenty's mobile failures (hover-only cell actions).
 * Treat it as a label for an already-labelled icon button.
 *
 * `TooltipProvider` is mounted once at the app root, AND this component carries one of its
 * own (SH-A9). Radix throws "`Tooltip` must be used within `TooltipProvider`" without an
 * ancestor provider, and since the shell round every icon-only button in the product has a
 * help tag - including the ones a unit test renders on their own and the ones a portal
 * puts outside the app tree. A nested provider is Radix's own supported composition; both
 * read the same delay, so the tag that appears under a button in a test appears after
 * exactly the same wait as the one in the product.
 */
export const TooltipProvider = TooltipPrimitive.Provider;

/**
 * HOW LONG A POINTER HAS TO REST. Apple states the trigger and the binding but publishes no
 * delay - "Treat delay as unspecified and use the system default" (wave 3 research, section
 * 5, after searching HIG Offering help, NSView.toolTip, NSHelpManager, SwiftUI help(_:) and
 * the archived OS X HIG, which now redirects away). This is the product's own value, held
 * in one place so the root and the per-button provider cannot drift.
 */
export const TOOLTIP_DELAY = 300;

export interface TooltipProps {
  readonly label: string;
  readonly children: ReactNode;
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * THE TAG SITS UNDER THE CONTROL (SH-A9). A help tag is placed relative to the element the
 * pointer is on, and macOS puts it below and beside the pointer rather than above it; with
 * `top` as the default every control in the window frame - which is where most icon-only
 * buttons in this product live - had to be collision-flipped by the browser before it
 * could be seen at all. Radix still flips when there is no room below.
 */
export function Tooltip({ label, children, side = 'bottom' }: TooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={TOOLTIP_DELAY}>
      <TooltipPrimitive.Root>
        {/*
          A HELP TAG IS A POINTER AFFORDANCE, AND ONLY THAT. Radix also opens a tooltip
          when its trigger takes KEYBOARD focus, and that is what broke the moment every
          icon-only button in the product got one (SH-A9): a dialog autofocuses its close
          button, the tag opened, and the tag then swallowed the Escape that was meant for
          the dialog - measured in `Dialog.test.tsx`, "returns focus to whatever opened it"
          failed with the dialog still on screen. Nothing is lost by it: the control's name
          is its `aria-label`, which is what a screen reader reads, and this component's own
          contract has said since 2026-07 that a tooltip never carries what a non-pointer
          user needs. Radix composes the caller's handler first and skips its own once the
          default is prevented, so this replaces the focus opening rather than racing it.
        */}
        <TooltipPrimitive.Trigger asChild onFocus={(event) => event.preventDefault()}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            /**
             * IT STAYS ON THE SCREEN, and this is what the gallery's edge column found: a
             * tooltip whose label is a sentence was 478px wide at a 375px viewport and hung
             * 103px off the left edge, because nothing bounded it. `collisionPadding` is what
             * every other floating panel here already has; the cap is the tooltip's own -
             * past about sixteen characters wide a tooltip is a paragraph, and a paragraph is
             * not a mouse affordance.
             */
            collisionPadding={8}
            className={`${MOTION.fade} chrome-raised layer-popover max-w-[min(16rem,calc(100vw-2*var(--gutter)))] rounded-control border border-line px-2 py-1 text-xs text-ink shadow-popover`}
          >
            {label}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
